<script setup lang="ts">
import Item from '../Item/index.vue'
import type { Topic } from '~~/src/types/topic'

const props = defineProps<{
  topics: Topic[]
  isBubbles?: boolean
  useAsFilter?: boolean
  activeId?: number
}>()

defineEmits<{
  (event: 'selectTopic', value?: Topic): void
}>()

const { activeId } = toRefs(props)

type Position = 'left' | 'right' | 'both' | 'none'
const fadePosition = ref<Position>('right')
const suggestedTopicRef = ref<HTMLUListElement>()

function handleTopicListScroll() {
  if (!suggestedTopicRef.value)
    return

  const { scrollLeft, scrollWidth, clientWidth } = suggestedTopicRef.value

  const maxScrollLeft = scrollWidth - clientWidth

  if (maxScrollLeft < 300)
    fadePosition.value = 'none'
  else if (scrollLeft > 0 && scrollLeft < maxScrollLeft)
    fadePosition.value = 'both'
  else if (scrollLeft > 0)
    fadePosition.value = 'left'
  else if (scrollLeft < maxScrollLeft)
    fadePosition.value = 'right'
  else
    fadePosition.value = 'none'
}

const route = useRoute()
const routeId = computed(() => asArray(route.params.id)[0])
function handleTopcListDirectionScroll(e: MouseEvent, direction: Position) {
  e.preventDefault()

  if (!suggestedTopicRef.value)
    return

  const scrollSize = 300

  suggestedTopicRef.value.scrollTo({
    left: suggestedTopicRef.value.scrollLeft + (direction === 'left' ? -scrollSize : scrollSize),
    behavior: 'smooth',
  })
}
</script>

<template>
  <div
    v-if="topics.length"
    class="relative w-full overflow-x-auto max-w-[95vdw] mx-a"
  >
    <button v-if="fadePosition === 'left' || fadePosition === 'both'" absolute top="50%" left-0 translate-y="-50%" class="hidden md:block h-6 w-6 z-1" i-mdi:chevron-left @click="handleTopcListDirectionScroll($event, 'left')" />
    <button v-if="fadePosition === 'right' || fadePosition === 'both'" absolute top="50%" right-0 translate-y="-50%" class="hidden md:block h-6 w-6 z-1" i-mdi:chevron-right @click="handleTopcListDirectionScroll($event, 'right')" />

    <ul
      ref="suggestedTopicRef"
      flex md="items-center"
      w-full h-full
      overflow-x-auto no-scrollbar
      :class="{
        'gap-2': isBubbles,
        'fadeOnLeft': fadePosition === 'left',
        'fadeOnRight': fadePosition === 'right',
        'fadeHorizontally': fadePosition === 'both',
      }"
      @scroll="handleTopicListScroll"
    >
      <li class="flex-shrink-0">
        <Item
          :topic="undefined"
          :is-active="!activeId && route.path === '/'"
          :is-bubbles="isBubbles"
          :use-as-filter="useAsFilter"
          @click="(t) => $emit('selectTopic', t)"
        />
      </li>

      <template v-for="topic in topics" :key="topic.id">
        <li v-if="!useAsFilter || topic.nElementals" class="flex-shrink-0">
          <Item
            :topic="topic"
            :is-active="activeId === topic.id || topic.id === parseInt(routeId) || topic.url === routeId"
            :is-bubbles="isBubbles"
            :use-as-filter="useAsFilter"
            @click="(t) => $emit('selectTopic', t)"
          />
        </li>
      </template>
    </ul>
  </div>
</template>
